/**
 * Onglet Import paiements : upload xlsx, preview détaillée, validation.
 * Modes : fusion (défaut, sécurisé) | remplacer (radical).
 */
import React, { useState, useRef } from 'react';
import {
  useImportPaiementPreviewMutation,
  useImportPaiementApplyMutation,
  PreviewPaiementResponse,
  ApplyPaiementResponse,
} from '../../services/portailAdminApi';

const PERIODES = [
  // Année académique : novembre → août + inscription + examen.
  { code: 'INSC', label: 'Inscription / Réinscription' },
  { code: 'NOV', label: 'Novembre' },
  { code: 'DEC', label: 'Décembre' },
  { code: 'JAN', label: 'Janvier' },
  { code: 'FEV', label: 'Février' },
  { code: 'MAR', label: 'Mars' },
  { code: 'AVR', label: 'Avril' },
  { code: 'MAI', label: 'Mai' },
  { code: 'JUIN', label: 'Juin' },
  { code: 'JUIL', label: 'Juillet' },
  { code: 'AOUT', label: 'Août' },
  { code: 'SEP', label: 'Septembre' },
  { code: 'OCT', label: 'Octobre' },
  { code: 'EXAM', label: 'Examen (2 semestres — 30 000 FCFA)' },
];

function formatMoney(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n)) return s;
  return new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
}

function formatDelta(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n)) return s;
  const formatted = new Intl.NumberFormat('fr-FR').format(Math.abs(n));
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `−${formatted}`;
  return '0';
}

type Mode = 'fusion' | 'remplacer';

export const ImportPaiements: React.FC = () => {
  const [periode, setPeriode] = useState('MAR');
  const [fichier, setFichier] = useState<File | null>(null);
  const [seuil, setSeuil] = useState<string>('');
  const [mode, setMode] = useState<Mode>('fusion');
  const [forcerPole, setForcerPole] = useState(false);
  const [preview, setPreview] = useState<PreviewPaiementResponse | null>(null);
  const [applied, setApplied] = useState<ApplyPaiementResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSection, setShowSection] = useState<'maj' | 'nouveaux' | 'conserves' | 'alertes' | 'apercu'>('apercu');
  const inputRef = useRef<HTMLInputElement>(null);

  const [previewMut, { isLoading: previewLoading }] = useImportPaiementPreviewMutation();
  const [applyMut, { isLoading: applyLoading }] = useImportPaiementApplyMutation();

  const handleFile = (f: File | null) => {
    setFichier(f);
    setPreview(null);
    setApplied(null);
    setError(null);
  };

  const buildFormData = (action: 'preview' | 'apply'): FormData => {
    const fd = new FormData();
    fd.append('fichier', fichier!);
    fd.append('periode', periode);
    fd.append('action', action);
    fd.append('mode', mode);
    if (forcerPole) fd.append('forcer_changement_pole', '1');
    if (seuil.trim()) fd.append('seuil_a_jour', seuil.trim());
    return fd;
  };

  const doPreview = async () => {
    if (!fichier) return;
    setError(null);
    setApplied(null);
    try {
      const res = await previewMut(buildFormData('preview')).unwrap();
      setPreview(res);
      setShowSection('apercu');
    } catch (e: any) {
      const detail = e?.data?.detail
        || (e?.data ? JSON.stringify(e.data).slice(0, 500) : null)
        || e?.message
        || 'Erreur lors du parsing';
      setError(detail);
    }
  };

  const doApply = async () => {
    if (!fichier || !preview) return;
    const diff = preview.diff?.totaux;
    let msg = `Importer ${preview.nb_lignes} lignes pour la période ${periode} ?\n\n`;
    msg += `Mode : ${mode === 'fusion' ? 'FUSION sécurisée' : 'REMPLACEMENT complet du pôle'}\n`;
    if (diff) {
      msg += `\n• ${diff.nb_nouveaux} étudiant(s) nouveau(x)`;
      msg += `\n• ${diff.nb_maj} mise(s) à jour de montant`;
      msg += `\n• ${diff.nb_inchanges} inchangé(s)`;
      if (mode === 'fusion') {
        msg += `\n• ${diff.nb_conserves} étudiant(s) ABSENT(S) du fichier mais conservé(s)`;
      } else {
        msg += `\n\n⚠ ATTENTION : ${diff.nb_conserves} étudiants présents en BDD (pas dans ce fichier) seront SUPPRIMÉS.`;
      }
      if (diff.nb_alertes_pole) {
        msg += `\n• ${diff.nb_alertes_pole} alerte(s) pôle ${forcerPole ? '(SERONT FORCÉES)' : '(préservées)'}`;
      }
    }
    if (seuil.trim()) msg += `\n\nSeuil "à jour" : ≥ ${seuil} FCFA`;
    msg += `\n\nUn backup sera créé.`;

    if (!window.confirm(msg)) return;
    setError(null);
    try {
      const res = await applyMut(buildFormData('apply')).unwrap();
      setApplied(res);
      setPreview(null);
    } catch (e: any) {
      setError(e?.data?.detail || 'Erreur lors de l\'application');
    }
  };

  const reset = () => {
    setFichier(null);
    setPreview(null);
    setApplied(null);
    setError(null);
    setSeuil('');
    setMode('fusion');
    setForcerPole(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const showSeuilHint = periode === 'EXAM' && (preview as any)?.pole_detecte === 'CJU' && !seuil.trim();
  const diff = preview?.diff;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Importer un fichier de paiements
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Période
            </label>
            <select
              value={periode}
              onChange={(e) => setPeriode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {PERIODES.map((p) => (
                <option key={p.code} value={p.code}>{p.code} — {p.label}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fichier Excel (.xlsx)
            </label>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-500 dark:text-gray-400
                file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                file:text-sm file:font-medium
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                dark:file:bg-blue-900/30 dark:file:text-blue-300"
            />
            {fichier && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {fichier.name} — {(fichier.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
        </div>

        {/* Mode d'import */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mode d'import
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition ${
              mode === 'fusion'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-300'
                : 'border-gray-300 dark:border-gray-600'
            }`}>
              <input
                type="radio" name="mode" value="fusion"
                checked={mode === 'fusion'}
                onChange={() => setMode('fusion')}
                className="mt-1"
              />
              <div className="ml-3">
                <div className="font-medium text-sm text-gray-900 dark:text-white">
                  ✅ Fusion sécurisée <span className="text-xs text-green-600">(recommandé)</span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Met à jour les paiements des étudiants présents dans le fichier. Les étudiants
                  déjà en BDD mais <strong>absents du fichier sont conservés</strong>. Idéal pour
                  des mises à jour incrémentales (paiements quotidiens).
                </div>
              </div>
            </label>
            <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition ${
              mode === 'remplacer'
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20 ring-1 ring-red-300'
                : 'border-gray-300 dark:border-gray-600'
            }`}>
              <input
                type="radio" name="mode" value="remplacer"
                checked={mode === 'remplacer'}
                onChange={() => setMode('remplacer')}
                className="mt-1"
              />
              <div className="ml-3">
                <div className="font-medium text-sm text-gray-900 dark:text-white">
                  ⚠️ Remplacement complet
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  <strong>SUPPRIME tous les paiements existants</strong> du pôle pour cette
                  période, puis insère ceux du fichier. À utiliser uniquement quand le fichier
                  est exhaustif et fait autorité.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Option avancée : forcer changement pôle */}
        {mode === 'fusion' && (
          <div className="mb-4">
            <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={forcerPole}
                onChange={(e) => setForcerPole(e.target.checked)}
                className="mr-2"
              />
              <span>
                Forcer le changement de pôle des étudiants existants
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  (par défaut, le pôle d'un enregistrement existant est préservé)
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Seuil */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Seuil minimum « à jour » (FCFA) — <span className="text-gray-500 font-normal">optionnel</span>
          </label>
          <div className="flex gap-2 items-start">
            <input
              type="number"
              value={seuil}
              min="0"
              step="500"
              onChange={(e) => setSeuil(e.target.value)}
              placeholder="Laissez vide pour utiliser le solde habituel"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            {periode === 'EXAM' && (
              <button
                type="button"
                onClick={() => setSeuil('15000')}
                className="px-3 py-2 text-xs bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 text-blue-700 dark:text-blue-300 rounded-lg whitespace-nowrap font-medium"
              >
                Pré-remplir 15 000
              </button>
            )}
          </div>
          {showSeuilHint && (
            <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-700 dark:text-orange-300">
              💡 Pôle Commerce/Juridique détecté + frais EXAM : pensez à saisir <strong>15 000</strong> comme seuil.
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={doPreview}
            disabled={!fichier || previewLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewLoading ? 'Analyse en cours...' : '🔍 Analyser le fichier'}
          </button>
          {(preview || applied) && (
            <button
              onClick={reset}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg"
            >
              ↩ Recommencer
            </button>
          )}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            ❌ {error}
          </div>
        )}
      </div>

      {preview && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Aperçu de l'import — {periode}
          </h3>

          {/* KPIs globaux du fichier */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">Étudiants fichier</div>
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{preview.nb_lignes}</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
              <div className="text-xs text-green-600 dark:text-green-400 mb-1">À jour</div>
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">{preview.nb_a_jour}</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
              <div className="text-xs text-red-600 dark:text-red-400 mb-1">En dette</div>
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">{preview.nb_en_dette}</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
              <div className="text-xs text-orange-600 dark:text-orange-400 mb-1">Reste à percevoir</div>
              <div className="text-lg font-bold text-orange-700 dark:text-orange-300">{formatMoney(preview.totaux.du)}</div>
            </div>
          </div>

          {/* Récap diff (impact sur la BDD) */}
          {diff && (
            <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                Impact sur la base de données ({mode === 'fusion' ? 'mode fusion' : 'mode remplacement'})
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <button
                  onClick={() => setShowSection('nouveaux')}
                  className={`text-left p-3 rounded border ${
                    showSection === 'nouveaux'
                      ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-400 ring-2 ring-purple-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-xs text-purple-700 dark:text-purple-300">🆕 Nouveaux</div>
                  <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{diff.totaux.nb_nouveaux}</div>
                </button>
                <button
                  onClick={() => setShowSection('maj')}
                  className={`text-left p-3 rounded border ${
                    showSection === 'maj'
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400 ring-2 ring-blue-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-xs text-blue-700 dark:text-blue-300">🔄 MAJ montant</div>
                  <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{diff.totaux.nb_maj}</div>
                </button>
                <div className="p-3 rounded border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500">⏸ Inchangés</div>
                  <div className="text-xl font-bold text-gray-600 dark:text-gray-400">{diff.totaux.nb_inchanges}</div>
                </div>
                <button
                  onClick={() => setShowSection('conserves')}
                  className={`text-left p-3 rounded border ${
                    showSection === 'conserves'
                      ? mode === 'remplacer'
                        ? 'bg-red-100 dark:bg-red-900/30 border-red-400 ring-2 ring-red-300'
                        : 'bg-orange-100 dark:bg-orange-900/30 border-orange-400 ring-2 ring-orange-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-xs">
                    {mode === 'remplacer'
                      ? <span className="text-red-700 dark:text-red-300">🗑 Seront supprimés</span>
                      : <span className="text-orange-700 dark:text-orange-300">💾 Conservés</span>
                    }
                  </div>
                  <div className={`text-xl font-bold ${
                    mode === 'remplacer'
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-orange-700 dark:text-orange-300'
                  }`}>{diff.totaux.nb_conserves}</div>
                </button>
                <button
                  onClick={() => setShowSection('alertes')}
                  className={`text-left p-3 rounded border ${
                    showSection === 'alertes'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400 ring-2 ring-yellow-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-xs text-yellow-700 dark:text-yellow-300">⚠ Alertes pôle</div>
                  <div className="text-xl font-bold text-yellow-700 dark:text-yellow-300">{diff.totaux.nb_alertes_pole}</div>
                </button>
              </div>

              {/* Warnings */}
              {mode === 'remplacer' && diff.totaux.nb_conserves > 0 && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-xs text-red-800 dark:text-red-200">
                  ⚠ <strong>{diff.totaux.nb_conserves} étudiants</strong> présents en BDD pour cette période/pôle mais
                  absents du fichier vont être <strong>SUPPRIMÉS</strong>. Vérifiez la liste avant d'appliquer.
                </div>
              )}
              {mode === 'fusion' && diff.totaux.nb_alertes_pole > 0 && (
                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-xs text-yellow-800 dark:text-yellow-200">
                  💡 <strong>{diff.totaux.nb_alertes_pole} étudiants</strong> du fichier sont déjà enregistrés dans un autre pôle.
                  En mode fusion, leur pôle d'origine est <strong>préservé</strong>. Cochez « Forcer le changement de pôle » si nécessaire.
                </div>
              )}

              <div className="mt-3 flex gap-2 text-xs">
                <button
                  onClick={() => setShowSection('apercu')}
                  className={`px-3 py-1 rounded ${
                    showSection === 'apercu'
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  Aperçu fichier
                </button>
              </div>
            </div>
          )}

          {/* Section dynamique selon onglet */}
          {showSection === 'apercu' && (
            <>
              <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                Aperçu des {preview.echantillon.length} premières lignes (sur {preview.nb_lignes}) :
              </div>
              <DiffTable rows={preview.echantillon} type="apercu" />
            </>
          )}

          {showSection === 'nouveaux' && diff && (
            <>
              <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                {diff.totaux.nb_nouveaux} étudiant(s) à créer (aperçu : {diff.nouveaux_apercu.length}) :
              </div>
              <DiffTable rows={diff.nouveaux_apercu} type="nouveaux" />
            </>
          )}

          {showSection === 'maj' && diff && (
            <>
              <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                {diff.totaux.nb_maj} mise(s) à jour de montant (aperçu : {diff.maj_montants_apercu.length}) :
              </div>
              <DiffTable rows={diff.maj_montants_apercu} type="maj" />
            </>
          )}

          {showSection === 'conserves' && diff && (
            <>
              <div className={`mb-2 text-sm ${mode === 'remplacer' ? 'text-red-700 dark:text-red-300 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
                {mode === 'remplacer' ? '🗑 SERONT SUPPRIMÉS' : '💾 Conservés (pas dans le fichier)'} :
                {' '}{diff.totaux.nb_conserves} étudiant(s) (aperçu : {diff.conserves_apercu.length})
              </div>
              <DiffTable rows={diff.conserves_apercu} type="conserves" />
            </>
          )}

          {showSection === 'alertes' && diff && (
            <>
              <div className="mb-2 text-sm text-yellow-700 dark:text-yellow-300 font-semibold">
                ⚠ Étudiants déjà enregistrés dans un autre pôle ({diff.totaux.nb_alertes_pole})
              </div>
              <DiffTable rows={diff.alertes_pole_apercu} type="alertes" />
            </>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={doApply}
              disabled={applyLoading}
              className={`px-6 py-2 text-white rounded-lg disabled:opacity-50 font-medium ${
                mode === 'remplacer'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {applyLoading
                ? 'Application...'
                : mode === 'remplacer'
                  ? `⚠ Confirmer remplacement (${preview.nb_lignes})`
                  : `✓ Confirmer fusion (${preview.nb_lignes})`}
            </button>
          </div>
        </div>
      )}

      {applied && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">✓</span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-2">
                Import appliqué avec succès
              </h3>
              <ul className="text-sm text-green-700 dark:text-green-200 space-y-1">
                <li>• Période : <strong>{applied.periode}</strong> · Mode : <strong>{applied.mode || 'fusion'}</strong> · Pôle : <strong>{applied.pole || 'TOUS'}</strong></li>
                <li>• {applied.nb_lignes} étudiants traités</li>
                {applied.nb_crees !== undefined && <li>• 🆕 {applied.nb_crees} créé(s) · 🔄 {applied.nb_majs} mis à jour</li>}
                {(applied.nb_supprimes_avant_insert ?? 0) > 0 && (
                  <li>• 🗑 {applied.nb_supprimes_avant_insert} ancien(s) supprimé(s) avant insert (mode remplacement)</li>
                )}
                {(applied.nb_conserves ?? 0) > 0 && (
                  <li>• 💾 {applied.nb_conserves} étudiant(s) déjà en BDD conservé(s) (mode fusion)</li>
                )}
                {(applied.nb_pole_force ?? 0) > 0 && (
                  <li>• ⚠ {applied.nb_pole_force} pôle(s) forcé(s)</li>
                )}
                <li>• {applied.nb_a_jour} à jour, {applied.nb_en_dette} en dette</li>
                <li>• Total dû : {formatMoney(applied.totaux.du)}</li>
                <li className="text-xs opacity-75">Backup : {applied.backup_path}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DiffTable: React.FC<{ rows: any[]; type: 'apercu' | 'nouveaux' | 'maj' | 'conserves' | 'alertes' }> = ({ rows, type }) => {
  if (rows.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/30 rounded">
        Aucun élément à afficher.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
            {type === 'alertes' ? (
              <>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pôle existant</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pôle fichier</th>
              </>
            ) : (
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
            )}
            {type === 'apercu' && (
              <>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Frais</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payé</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Solde</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
              </>
            )}
            {type === 'nouveaux' && (
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payé fichier</th>
            )}
            {type === 'maj' && (
              <>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payé avant</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payé après</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Δ</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">À jour</th>
              </>
            )}
            {type === 'conserves' && (
              <>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payé BDD</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Solde</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((r, idx) => (
            <tr key={`${r.matricule}-${idx}`}>
              <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">{r.matricule}</td>
              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{r.nom} {r.prenoms || ''}</td>
              {type === 'alertes' ? (
                <>
                  <td className="px-3 py-2 text-orange-700 dark:text-orange-300 font-medium">{r.pole_existant}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.pole_fichier}</td>
                </>
              ) : (
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.classe || '—'}</td>
              )}
              {type === 'apercu' && (
                <>
                  <td className="px-3 py-2 text-right">{formatMoney(r.frais_attendus)}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(r.frais_payes)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatMoney(r.solde)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.est_a_jour ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">✓</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300">⚠</span>
                    )}
                  </td>
                </>
              )}
              {type === 'nouveaux' && (
                <td className="px-3 py-2 text-right text-purple-700 dark:text-purple-300 font-medium">
                  {formatMoney(r.frais_payes_nouveau)}
                </td>
              )}
              {type === 'maj' && (
                <>
                  <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{formatMoney(r.frais_payes_avant)}</td>
                  <td className="px-3 py-2 text-right text-blue-700 dark:text-blue-300 font-semibold">{formatMoney(r.frais_payes_apres)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${
                    parseFloat(r.delta_paye) > 0 ? 'text-green-700 dark:text-green-300'
                      : parseFloat(r.delta_paye) < 0 ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-500'
                  }`}>{formatDelta(r.delta_paye)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-xs">
                      {r.a_jour_avant ? '✓' : '⚠'} → {r.a_jour_apres ? '✓' : '⚠'}
                    </span>
                  </td>
                </>
              )}
              {type === 'conserves' && (
                <>
                  <td className="px-3 py-2 text-right">{formatMoney(r.frais_payes)}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(r.solde)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.est_a_jour ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">✓</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300">⚠</span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
